import type {
  ApiSuccess,
  CreateLotteryReportRequest,
  ListLotteryStockistsQuery,
  LotteryBatchDetail,
  LotteryBatchStatus,
  LotteryBatchSummary,
  LotteryDataGaps,
  LotteryDownloadLink,
  LotteryOverview,
  LotteryPageName,
  LotteryReference,
  LotteryReportJob,
  LotteryReportOptions,
  LotteryReferenceGaps,
  LotteryReviewQueue,
  LotteryReviewScheme,
  LotteryReviewStockist,
  LotteryStockistDetail,
  LotteryUploadResult,
  UpdateLotterySchemeRequest,
  UpdateLotteryStockistRequest,
} from '@todo/shared';
import { api, get, getPaged, patch, post } from './client';

/**
 * Typed wrappers around /api/lottery (Phase 4a). Every route needs the
 * lottery.upload permission; the server checks it on every request.
 */
export const lotteryApi = {
  /**
   * POST /api/lottery/uploads - the File IS the body (no FormData; the API
   * reads it with express.raw). Validation runs before the response, so
   * give it longer than the default 20 s.
   */
  async upload(file: File, onProgress?: (percent: number) => void): Promise<LotteryBatchDetail> {
    const res = await api.post<ApiSuccess<LotteryUploadResult>>('/lottery/uploads', file, {
      params: { fileName: file.name },
      headers: { 'Content-Type': 'application/octet-stream' },
      timeout: 120_000,
      onUploadProgress: (event) => {
        if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
      },
    });
    return res.data.data.batch;
  },

  list(query: { page?: number; pageSize?: number; status?: LotteryBatchStatus; from?: string; to?: string } = {}): Promise<ApiSuccess<LotteryBatchSummary[]>> {
    return getPaged<LotteryBatchSummary[]>('/lottery/uploads', query as Record<string, unknown>);
  },

  get(id: string): Promise<LotteryBatchDetail> {
    return get<LotteryBatchDetail>(`/lottery/uploads/${encodeURIComponent(id)}`);
  },

  /** The commit can take a while on a big file. */
  async confirm(id: string): Promise<LotteryBatchDetail> {
    const res = await api.post<ApiSuccess<LotteryBatchDetail>>(
      `/lottery/uploads/${encodeURIComponent(id)}/confirm`,
      undefined,
      { timeout: 120_000 },
    );
    return res.data.data;
  },

  discard(id: string): Promise<LotteryBatchDetail> {
    return post<LotteryBatchDetail>(`/lottery/uploads/${encodeURIComponent(id)}/discard`);
  },

  /** A short-lived link to the original file. */
  downloadLink(id: string): Promise<LotteryDownloadLink> {
    return get<LotteryDownloadLink>(`/lottery/uploads/${encodeURIComponent(id)}/download`);
  },

  gaps(): Promise<LotteryDataGaps> {
    return get<LotteryDataGaps>('/lottery/gaps');
  },

  /** What the stockist / scheme lists still miss (the overview notice; lottery.upload). */
  referenceGaps(): Promise<LotteryReferenceGaps> {
    return get<LotteryReferenceGaps>('/lottery/reference-gaps');
  },

  reference(): Promise<LotteryReference> {
    return get<LotteryReference>('/lottery/reference');
  },

  schemes(search?: string): Promise<LotteryReviewScheme[]> {
    return get<LotteryReviewScheme[]>('/lottery/schemes', search ? { search } : undefined);
  },

  stockists(query: ListLotteryStockistsQuery = {}): Promise<ApiSuccess<LotteryReviewStockist[]>> {
    return getPaged<LotteryReviewStockist[]>('/lottery/stockists', query as Record<string, unknown>);
  },

  updateScheme(id: string, body: UpdateLotterySchemeRequest): Promise<LotteryReviewQueue> {
    return patch<LotteryReviewQueue>(`/lottery/schemes/${encodeURIComponent(id)}`, body);
  },

  updateStockist(id: string, body: UpdateLotteryStockistRequest): Promise<LotteryReviewQueue> {
    return patch<LotteryReviewQueue>(`/lottery/stockists/${encodeURIComponent(id)}`, body);
  },
};

/**
 * The Lottery page and its reports (Phase 7a). Every route needs
 * lottery.view; the server checks it on every request. Every figure comes
 * from the rpt_* reporting layer (the same numbers the chatbot sees).
 */
export const lotteryViewApi = {
  overview(scope: string, days: 30 | 90): Promise<LotteryOverview> {
    return get<LotteryOverview>('/lottery/overview', { scope, days });
  },

  stockist(id: string, scope: string): Promise<LotteryStockistDetail> {
    return get<LotteryStockistDetail>(`/lottery/overview/stockists/${encodeURIComponent(id)}`, { scope });
  },

  /** Once per page (tab) open: the PAGE_VIEW audit row. */
  async pageView(page: LotteryPageName): Promise<void> {
    await api.post('/lottery/page-views', { page });
  },

  reportOptions(): Promise<LotteryReportOptions> {
    return get<LotteryReportOptions>('/lottery/reports/options');
  },

  myReports(): Promise<LotteryReportJob[]> {
    return get<LotteryReportJob[]>('/lottery/reports');
  },

  latestWeekly(): Promise<LotteryReportJob[]> {
    return get<LotteryReportJob[]>('/lottery/reports/latest-weekly');
  },

  requestReport(body: CreateLotteryReportRequest): Promise<LotteryReportJob> {
    return post<LotteryReportJob>('/lottery/reports', body);
  },

  report(id: string): Promise<LotteryReportJob> {
    return get<LotteryReportJob>(`/lottery/reports/${encodeURIComponent(id)}`);
  },

  /** A short-lived link to a READY report. */
  reportLink(id: string): Promise<LotteryDownloadLink> {
    return get<LotteryDownloadLink>(`/lottery/reports/${encodeURIComponent(id)}/download`);
  },
};
